// React
import { useMemo, useState } from "react";

// MUI
import { Stack, Typography } from "@mui/material";

// Hooks
import { useCitContextData } from "@DataPages/context/cit/CitContext";

// Utils
import { DealShape, generateAnalyticsData } from "@DataPages/helpers/financeManagement/cit/analytics";
import { numberToReadableFormat } from "@Common/helpers/number";
import { formatNumberCompact } from "@Common/helpers/number-new";

// Components
import { AnimatedNumber } from "@DataPages/components/FniManagement/components/CIT/AnimatedNumber";
import MultiGroupedBarChartAnalyticsSkeleton from "@DataPages/layouts/financeManagement/skeletons/MultiGroupedBarChartAnalyticsSkeleton";
import MultiBarChartGraphLegend from "@DataPages/layouts/financeManagement/widgets/MultiBarChartGraphLegend";
import GroupedBarChart from "@Layouts/components/Charts/GroupedBarChart/GroupedBarChart";

// Types
import { Nullable } from "@Common/interfaces/types";
import { ISystemSettings } from "@Settings/types/Common/model";
import { IFinanceDeal } from "@DataPages/types/financeDeals/model";
import { GroupedBarConfig } from "@Layouts/components/Charts/GroupedBarChart/types";
import { IPeriodTreeSelection } from "@Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { IAnalyticsData } from "@DataPages/components/FniManagement/components/CIT/GradientBarGraph/GradientBarGraph";

type TFinanceDealsAnalyticsSection = Readonly<{
	selectedPayPeriod: IPeriodTreeSelection;
	isLoading: boolean;
	data: Nullable<IFinanceDeal[]>;
}>;

const colorByStatus = {
	delivered: "#FFB507",
	closed: "#2357BE",
	finalized: "#009245",
};

type TDealStatus = "delivered" | "closed" | "finalized";

function getGraphWidth(citSettings: ISystemSettings) {
	return parseInt((citSettings?.citGraphWidth ?? "").replace(" Days", ""), 10) || 0;
}

const FI_MANAGEMENT_BARS_DEFINITION: GroupedBarConfig<{
	delivered: number;
	closed: number;
	finalized: number;
}>[] = [
	{ dataKey: "delivered", color: "#FFB507", label: "Delivered" },
	{ dataKey: "closed", color: "#2357BE", label: "Closed" },
	{ dataKey: "finalized", color: "#009245", label: "Finalized" },
];

export default function FinanceDealsAnalyticsSection(props: TFinanceDealsAnalyticsSection) {
	const { selectedPayPeriod, data: financeDeals, isLoading } = props ?? {};

	const { citSettings } = useCitContextData();

	const [hoveredAge, setHoveredAge] = useState("");

	const citGraphWidth = getGraphWidth((citSettings ?? {}) as ISystemSettings);

	const maxValues = citSettings?.citMaxGraphValues ?? {
		maxUnfundedDollars: 5,
		maxUnfundedContracts: 200,
	};

	const { totalUnfundedDollars, totalUnfundedContracts } = useMemo(() => {
		const unfundedDollars = generateAnalyticsData({
			deals: financeDeals as DealShape[],
			graphWidth: citGraphWidth,
			propToUse: "amountFinanced",
		});

		const unfundedContracts = generateAnalyticsData({
			deals: financeDeals as DealShape[],
			graphWidth: citGraphWidth,
			propToUse: "dealStatus",
		});

		return { totalUnfundedDollars: unfundedDollars, totalUnfundedContracts: unfundedContracts };
	}, [financeDeals, citGraphWidth]);

	if (isLoading) {
		return <MultiGroupedBarChartAnalyticsSkeleton showSeverityBar={false} />;
	}

	const accumulateAnalyticValues = (data: IAnalyticsData[], prop: "quantity" | "amount") => {
		const property = prop === "amount" ? "exactAmount" : prop;
		return data.reduce((acc, cur) => acc + Number(cur?.[property]), 0);
	};

	/********************************************* HANDLERS ************************************************/
	const handleHoverBar = (age: string) => {
		setHoveredAge(age);
	};

	const handleHoverOutBar = () => {
		setHoveredAge("");
	};

	return (
		<Stack
			component={"section"}
			id={"f&i-deals-analytics"}
			spacing={3}
			justifyContent={"space-between"}
			alignItems={"center"}
			pt={4}
			pb={6.5}
			position={"relative"}
		>
			<Typography variant={"h3"} className={"page-title"} alignSelf={"self-start"} id={"f&i-all-deals-analytics-title"}>
				{selectedPayPeriod?.titleExtended} Deals Analytics
			</Typography>
			{/* Graphs */}
			<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
				<Stack spacing={7}>
					<Typography className={"!text-default !font-montserrat !text-2xl !font-medium"} component="h3">
						{"Unfunded Dollars: "} <sup>$</sup>
						<AnimatedNumber finalValue={accumulateAnalyticValues(totalUnfundedDollars ?? [], "amount")} />
					</Typography>
					<GroupedBarChart
						syncId="all-deals-analytics-grouped"
						data={totalUnfundedDollars}
						bars={FI_MANAGEMENT_BARS_DEFINITION}
						tooltip={{
							useCustomizedTooltip: true,
							showTotal: true,
							valueFormatter: (value, dataKey) => {
								return (
									<Stack direction={"row"} justifyContent={"space-between"}>
										<Typography
											className={`!text-${colorByStatus[dataKey as TDealStatus]} !font-sans !text-xs !font-bold`}
										>
											$
										</Typography>
										<Typography
											className={`!text-${colorByStatus[dataKey as TDealStatus]} !font-sans !text-xs !font-bold`}
										>
											{formatNumberCompact(value)}
										</Typography>
									</Stack>
								);
							},
						}}
						totalKey="total"
						parentBarColor="#ECF0F6"
						xAxis={{ dataKey: "age", label: "Transit (days)" }}
						yAxis={{
							label: "Amount ($)",
							maxValue: maxValues.maxUnfundedDollars,
							tickFormatter: (value) => `$ ${formatNumberCompact(value)}`,
						}}
						hoveredValue={hoveredAge}
						onBarHover={handleHoverBar}
						onBarLeave={handleHoverOutBar}
					/>
				</Stack>
				<Stack spacing={7}>
					<Typography className={"!text-default !font-montserrat !text-2xl !font-medium"} component="h3">
						{"Unfunded Contracts: "}
						<AnimatedNumber finalValue={accumulateAnalyticValues(totalUnfundedContracts ?? [], "quantity")} />
					</Typography>

					<GroupedBarChart
						syncId="all-deals-analytics-grouped"
						data={totalUnfundedContracts}
						bars={FI_MANAGEMENT_BARS_DEFINITION}
						tooltip={{
							useCustomizedTooltip: true,
							showTotal: true,
							valueFormatter: numberToReadableFormat,
						}}
						totalKey="total"
						parentBarColor="#ECF0F6"
						xAxis={{ dataKey: "age", label: "Transit (days)" }}
						yAxis={{
							label: "# of Contracts",
							maxValue: maxValues.maxUnfundedContracts,
						}}
						hoveredValue={hoveredAge}
						// Callbacks
						onBarHover={handleHoverBar}
						onBarLeave={handleHoverOutBar}
					/>
				</Stack>
			</Stack>
			<MultiBarChartGraphLegend />
		</Stack>
	);
}
